import { useState } from "react";
import clsx from "clsx";
import { Send, Loader2, ExternalLink } from "lucide-react";
import { api } from "../../api";
import { useToast } from "../../ui/toast";
import Md from "../Md";
import type { IntakeOut } from "./types";

// Rückfragen-Panel + Ausarbeitung (result_md) + Task-Links — VERBATIM aus
// ProjectIntake.tsx (sendAnswers Zeile 178-198 + Panel-JSX Zeile 222-306)
// extrahiert. Wird vom Intake-Tab (IntakeCard) UND von den aufgeklappten
// Kanban-„Ingest"-Karten genutzt. onAnswered feuert nach erfolgreichem PATCH.
export default function IntakeQuestions({
  intake,
  onAnswered,
}: {
  intake: IntakeOut;
  onAnswered: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Antwort-Draft pro Frage (id -> answer).
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (intake.questions || []).map((q) => [q.id, q.answer ?? ""])
    )
  );

  const hasQuestions = intake.status === "questions" && intake.questions.length > 0;

  const sendAnswers = async () => {
    setBusy(true);
    try {
      const questions = intake.questions.map((q) => ({
        id: q.id,
        q: q.q,
        options: q.options ?? null,
        answer: (answers[q.id] ?? "").trim() || null,
      }));
      await api.patch(`/projects/-/intakes/${intake.id}`, {
        questions,
        status: "answered",
      });
      toast.ok("Antworten gesendet");
      onAnswered();
    } catch (e: any) {
      toast.err("Antworten senden fehlgeschlagen", e?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {hasQuestions && (
        <div className="space-y-3 rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50/60 dark:bg-fuchsia-950/30 p-3">
          <div className="text-[11px] uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 font-semibold">
            Rückfragen des Planners
          </div>
          {intake.questions.map((q) => (
            <div key={q.id} className="space-y-1.5">
              <div className="text-sm text-text font-medium">{q.q}</div>
              {q.options && q.options.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() =>
                        setAnswers((a) => ({ ...a, [q.id]: opt }))
                      }
                      className={clsx(
                        "px-2 py-1 rounded-full text-xs border transition-colors",
                        answers[q.id] === opt
                          ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                          : "border-fuchsia-300 dark:border-fuchsia-700 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-100 dark:hover:bg-fuchsia-900/40"
                      )}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={answers[q.id] ?? ""}
                onChange={(e) =>
                  setAnswers((a) => ({ ...a, [q.id]: e.target.value }))
                }
                placeholder="Deine Antwort…"
                aria-label={`Antwort auf: ${q.q}`}
                className="w-full px-2 py-1.5 rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-surface text-sm"
              />
            </div>
          ))}
          <div className="flex justify-end">
            <button
              onClick={sendAnswers}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Antworten senden
            </button>
          </div>
        </div>
      )}

      {intake.result_md && (
        <div className="rounded-md border border-border bg-surface-2 p-3">
          <div className="text-[11px] uppercase tracking-wider text-text-3 mb-1">
            Ausarbeitung
          </div>
          <Md>{intake.result_md}</Md>
        </div>
      )}

      {intake.status === "planned" && intake.created_task_ids.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-wider text-text-3">
            Erstellte Aufgaben ({intake.created_task_ids.length})
          </div>
          <ul className="space-y-1">
            {intake.created_task_ids.map((tid) => (
              <li key={tid}>
                <a
                  href={`/tasks/${tid}`}
                  className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1 font-mono"
                >
                  <ExternalLink className="w-3 h-3" /> {tid.slice(0, 8)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
